import { useState } from "react";
import { useQuery } from "react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Card, Col, Form, InputGroup, Row, Spinner } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import Select from "react-select";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiDomain, mdiPlus, mdiTrashCanOutline } from "@mdi/js";

import { ModuleEntity, ModulePermissionEntity } from "../../config/defines";
import { modulePermissionCheck } from "../../config/utils";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { DomainEntityGetResponse } from "../../entities/DomainEntity";

import Layout from "../../components/Layout";
import { SelectGroupStartStyle, SelectOptions } from "../../config/select";
import { QuestionnaireGetQuestionnairesResponse } from "../../entities/QuestionnaireEntity";
import QuestionnaireService from "../../services/QuestionnaireService";

const toastTitle = "Edificação";

export default function EdificacaoFormulario() {
	const questionnaireService = new QuestionnaireService();

	const navigate = useNavigate();
	const { user } = useAuth();
	const { id } = useParams();
	const { handleToast } = useToast();

	const [formStatus, setFormStatus] = useState(id ? false : true);
	const [formSaving, setFormSaving] = useState(false);

	const queryQuestionnaires = useQuery<QuestionnaireGetQuestionnairesResponse[]>(["avaliacoes"], () => fetchDataQuestionnaires());
	const { data, isLoading, isFetching, isRefetching } = useQuery<DomainEntityGetResponse>(["edificio", id], () => fetchData(id), { enabled: !!id && !formStatus });

	const { register, control, handleSubmit, reset } = useForm({ defaultValues: data });
	const { fields, append, remove } = useFieldArray({ name: "domainOptions", control: control });

	async function fetchData(id: any) {
		let resp: any = await new Promise((resolve) => {
			setTimeout(() => {
				resolve({
					id: 1,
					title: "Edifício Green Tower",
					type: 1,
					typeDesc: "Intensidade",
					domainOptions: [
						{
							id: 1,
							title: "Estrutura A",
							order: 1,
							value: 26,
						},
						{
							id: 10,
							title: "Estrutura B",
							order: 0,
							value: 27,
						},
					],
				});
			}, 100);
		});
		reset(resp);
		return resp;
	}

	async function fetchDataQuestionnaires() {
		const resp = await questionnaireService.getQuestionnaires();
		return resp.data;
	}

	function handleAdicionar() {
		append({ id: undefined, title: "", order: fields.length, value: null });
	}

	function handleRemover(id: number, index: number) {
		remove(index);
	}

	return (
		<Layout>
			<Form
				onSubmit={handleSubmit((values) => {
					setFormSaving(true);
					handleToast(toastTitle, "Informações salvas com sucesso!", 5000);
					navigate("/edificacoes");
				})}
			>
				<h5 className="mt-4 mb-4 d-flex align-items-center fw-light">
					<Link to={"/edificacoes"} className="d-flex align-items-center text-decoration-none">
						<Icon path={mdiChevronLeft} size={1} className="me-1" /> <Icon path={mdiDomain} size={1} className="me-1" /> Formulário de Edificação
					</Link>
					{(isLoading || isFetching || isRefetching) && <Spinner size="sm" className="ms-1" variant="secondary" />}
					<div className="d-flex gap-2 float-right ms-auto" style={{ marginTop: -10, marginBottom: -10 }}>
						{modulePermissionCheck(user!, ModuleEntity.configuracoesDominios, ModulePermissionEntity.editar) && (
							<>
								{!formStatus && (
									<Button
										variant="light"
										className="rounded-3 shadow-sm"
										onClick={() => {
											setFormStatus(true);
										}}
									>
										Editar Informações
									</Button>
								)}
								{formStatus && (
									<>
										<Button
											variant="light"
											className="rounded-3 shadow-sm"
											type="button"
											onClick={() => {
												if (id) {
													setFormStatus(false);
												} else {
													navigate("/dominios");
												}
											}}
											disabled={formSaving}
										>
											Cancelar
										</Button>
										<Button variant="primary" className="rounded-3 shadow-sm" type="submit" disabled={formSaving}>
											{formSaving ? (
												<>
													<Spinner animation="border" size="sm" className="me-2" /> Salvando
												</>
											) : (
												"Salvar Informações"
											)}
										</Button>
									</>
								)}
							</>
						)}
					</div>
				</h5>

				<Card className="mb-4">
					<Card.Header className="d-flex align-items-center bg-white fs-5 fw-light p-3">Edificação</Card.Header>
					<Card.Body>
						<Form.Group className="mb-3" controlId="titulo">
							<Form.Label>Título</Form.Label>
							<Form.Control type="text" placeholder="Informe aqui" disabled={!formStatus} {...register("title", { required: true })} />
						</Form.Group>
					</Card.Body>
				</Card>

				<Card className="mb-4">
					<Card.Header className="d-flex align-items-center bg-white fs-5 fw-light p-3">Etapas</Card.Header>
					<Card.Body>
						{fields.map((domainOption, domainOptionIndex) => {
							const defaultType = queryQuestionnaires.data?.find((item) => {
								return item.id === domainOption.value;
							});
							return (
								<Row key={domainOption.id} className="mb-2">
									<Col>
										<InputGroup>
											<InputGroup.Text>
												<small className="text-muted">Titulo</small>
											</InputGroup.Text>
											<Form.Control type="text" placeholder="Informe aqui" disabled={!formStatus} {...register(`domainOptions.${domainOptionIndex}.title`)} />
										</InputGroup>
									</Col>
									<Col>
										<InputGroup>
											<InputGroup.Text>
												<small className="text-muted">Avaliação</small>
											</InputGroup.Text>
											<Select
												placeholder={"Selecione"}
												defaultValue={defaultType ? { label: defaultType.title, value: defaultType.id } : undefined}
												options={queryQuestionnaires.data?.map((item) => {
													return { label: item.title, value: item.id };
												})}
												// onChange={(value) => {
												// 	handleTipo(value, question.id!, questionIndex);
												// }}
												styles={SelectGroupStartStyle}
												isDisabled={!formStatus}
												{...SelectOptions}
											/>
										</InputGroup>
									</Col>
									<Col lg="auto">
										<Button
											variant="light"
											className="rounded-3 shadow-sm"
											onClick={() => {
												handleRemover(domainOption.id, domainOptionIndex);
											}}
										>
											<Icon path={mdiTrashCanOutline} size={1} className="text-danger" />
										</Button>
									</Col>
								</Row>
							);
						})}
					</Card.Body>
					<Card.Footer className="bg-white">
						<Button variant="light" className="d-flex rounded-3 shadow-sm" onClick={handleAdicionar}>
							<Icon path={mdiPlus} size={1} /> Adicionar Etapa
						</Button>
					</Card.Footer>
				</Card>

				{modulePermissionCheck(user!, ModuleEntity.configuracoesDominios, ModulePermissionEntity.editar) && (
					<div className="d-flex gap-2 mb-4">
						{!formStatus && (
							<Button
								variant="light"
								className="rounded-3 shadow-sm"
								onClick={() => {
									setFormStatus(true);
								}}
							>
								Editar Informações
							</Button>
						)}
						{formStatus && (
							<>
								<Button variant="primary" className="rounded-3 shadow-sm" type="submit" disabled={formSaving}>
									{formSaving ? (
										<>
											<Spinner animation="border" size="sm" className="me-2" /> Salvando
										</>
									) : (
										"Salvar Informações"
									)}
								</Button>
								<Button
									variant="light"
									className="rounded-3 shadow-sm"
									type="button"
									onClick={() => {
										if (id) {
											setFormStatus(false);
										} else {
											navigate("/edificacoes");
										}
									}}
									disabled={formSaving}
								>
									Cancelar
								</Button>
							</>
						)}
					</div>
				)}
			</Form>
		</Layout>
	);
}
